import { useMemo, useState } from "react"
import { Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

export type SlotTemplate = {
  day_of_week: string  // "monday", "tuesday", ...
  time_of_day: string  // "08:00", "14:30", ...
}

const DAYS: Array<{ key: string; label: string }> = [
  { key: "monday",    label: "Mon" },
  { key: "tuesday",   label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday",  label: "Thu" },
  { key: "friday",    label: "Fri" },
  { key: "saturday",  label: "Sat" },
  { key: "sunday",    label: "Sun" },
]

const DEFAULT_TIMES = ["08:00", "12:00", "16:00", "20:00"]

// Render a stored 24-hour "HH:MM" time as a 12-hour clock label with an
// AM/PM suffix: "08:00" → "8:00 AM", "12:00" → "12:00 PM", "16:00" →
// "4:00 PM", "00:30" → "12:30 AM". The underlying data stays in
// zero-padded 24-hour HH:MM so it sorts and serializes consistently.
const formatTimeLabel = (time: string): string => {
  const [hStr, m] = time.split(":")
  const h = Number(hStr)
  if (Number.isNaN(h)) return time
  const period = h >= 12 ? "PM" : "AM"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${m} ${period}`
}

type Props = {
  value: SlotTemplate[]
  onChange: (next: SlotTemplate[]) => void
  // Offer the default times as empty starter rows. Only ever true for a
  // brand-new channel — an existing channel shows exactly the times it has
  // saved, so an emptied schedule stays empty instead of looking like it
  // reverted to the defaults.
  seedDefaults?: boolean
}

// Visual weekly grid for editing a channel's slot_templates. Columns are
// day-of-week; rows are time-of-day. Each cell is a `toggle-button` showing
// the time — clicking toggles the slot on/off for that day. The time-of-day
// label *is* the toggle button, so we don't need a separate Time column.
export function SlotTemplateGrid({ value, onChange, seedDefaults = false }: Props) {
  // Rows the user has added this session that don't (yet) have any day
  // toggled on, so the empty row still shows while they pick days. Seeded
  // with the default times ONLY for a brand-new channel with no slots yet,
  // giving it some starter rows to toggle.
  //
  // We deliberately do NOT merge DEFAULT_TIMES into the displayed rows on
  // every render: doing so resurrected any default time the user had
  // deleted (delete row → save → the row reappears on the next mount),
  // which read as "the schedule reverted to the default times."
  const [extraTimes, setExtraTimes] = useState<string[]>(() =>
    seedDefaults && value.length === 0 ? DEFAULT_TIMES : [],
  )
  const [newTime, setNewTime] = useState("")

  // Displayed rows = every time present in the saved value, plus any empty
  // rows the user just added. Derived from `value` so a saved time always
  // has a visible row and a deleted time never does.
  const times = useMemo(() => {
    const fromValue = value.map((s) => s.time_of_day)
    return Array.from(new Set([...fromValue, ...extraTimes])).sort()
  }, [value, extraTimes])

  const has = (day: string, time: string) =>
    value.some((s) => s.day_of_week === day && s.time_of_day === time)

  const toggle = (day: string, time: string) => {
    if (has(day, time)) {
      onChange(value.filter((s) => !(s.day_of_week === day && s.time_of_day === time)))
    } else {
      onChange([...value, { day_of_week: day, time_of_day: time }])
    }
  }

  const addTime = () => {
    const t = newTime.trim()
    if (!/^\d{2}:\d{2}$/.test(t)) {
      window.alert("Use 24-hour format like 09:30")
      return
    }
    if (!times.includes(t)) {
      // Enable the new time on every day so it's an actual saved slot the
      // moment it's added — a time with no days toggled has nothing to
      // persist and would vanish on the next load. The user toggles off the
      // days they don't want. Keep it in extraTimes too so the row stays put
      // even if they then clear every day.
      setExtraTimes((prev) => [...prev, t])
      onChange([...value, ...DAYS.map((d) => ({ day_of_week: d.key, time_of_day: t }))])
    }
    setNewTime("")
  }

  const removeTime = (time: string) => {
    if (value.some((s) => s.time_of_day === time)) {
      if (!window.confirm(`Remove all ${formatTimeLabel(time)} slots? This deletes them from every day.`)) return
      onChange(value.filter((s) => s.time_of_day !== time))
    }
    setExtraTimes((prev) => prev.filter((t) => t !== time))
  }

  return (
    <div className="rounded-md border border-hairline bg-page">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-hairline text-xs uppercase tracking-wide text-ink-muted">
            {DAYS.map((d) => (
              <th key={d.key} className="px-1 py-2 text-center">{d.label}</th>
            ))}
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {times.map((time) => (
            <tr key={time} className="border-b border-hairline last:border-b-0">
              {DAYS.map((d) => (
                <td key={d.key} className="px-1 py-1.5 text-center">
                  <button
                    type="button"
                    className="toggle-button toggle-button-compact w-full font-mono"
                    aria-pressed={has(d.key, time)}
                    onClick={() => toggle(d.key, time)}
                    aria-label={`${d.label} ${formatTimeLabel(time)}`}
                  >
                    {formatTimeLabel(time)}
                  </button>
                </td>
              ))}
              <td className="px-2 py-1.5 text-right">
                <button
                  type="button"
                  onClick={() => removeTime(time)}
                  className="cursor-pointer text-ink-muted hover:text-danger-display"
                  aria-label={`Remove ${formatTimeLabel(time)}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center gap-2 border-t border-hairline px-3 py-2">
        <Input
          type="time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          placeholder="HH:MM"
          className="max-w-32"
        />
        <Button type="button" variant="secondary" size="sm" onClick={addTime}>
          <Plus className="h-3 w-3" />
          Add time
        </Button>
      </div>
    </div>
  )
}
